<script setup>
// BonBoZ: bảng thiết bị của trang Thông báo đẩy — trạng thái vòng đời của từng
// máy (#230) và nút "Gỡ máy này" để thôi gửi push tới máy đã chết.
//
// Mỗi máy một dòng: bảng cuộn ngang, chữ dài (hệ điều hành, lý do, lỗi đăng ký)
// bị cắt, rê chuột xem đủ. Máy cũ (đã gỡ, hết hạn, đã thay) nằm cuối, mờ đi và
// gập lại mặc định (#232) — chúng không còn nhận gì nhưng giữ để biết vì sao.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PushReportAPI from 'dashboard/api/bonboz/pushReport';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { dateTime } from './pushReportFormat';

const props = defineProps({
  devices: { type: Array, required: true },
});

const emit = defineEmits(['retired']);

const { t, te } = useI18n();

// Server cũ chưa gửi `inactive`: nhận ra máy cũ qua trạng thái.
const INACTIVE_STATUSES = ['retired', 'replaced', 'expired'];
const isInactive = device =>
  Boolean(device.inactive) || INACTIVE_STATUSES.includes(device.status);

const activeDevices = computed(() =>
  props.devices.filter(device => !isInactive(device))
);
// Máy mới thôi dùng gần đây nhất lên trước.
const inactiveDevices = computed(() =>
  props.devices
    .filter(isInactive)
    .sort((a, b) =>
      (b.status_changed_at || '').localeCompare(a.status_changed_at || '')
    )
);

const showInactive = ref(false);
const visibleDevices = computed(() =>
  showInactive.value
    ? [...activeDevices.value, ...inactiveDevices.value]
    : activeDevices.value
);

const displayDate = iso => dateTime(iso) || '—';

const STATUS_CHIP_CLASSES = {
  active: 'bg-n-teal-3 text-n-teal-11',
  unreachable: 'bg-n-amber-3 text-n-amber-11',
  blocked_in_os: 'bg-n-ruby-3 text-n-ruby-11',
};

const statusChipClass = status =>
  STATUS_CHIP_CLASSES[status] || 'bg-n-alpha-2 text-n-slate-11';

// Mã lạ (server thêm trạng thái/lý do mới) thì hiện nguyên mã.
const translatedCode = (group, code) => {
  if (!code) return '—';
  const key = `BONBOZ.PUSH_REPORT.${group}.${code.toUpperCase()}`;
  return te(key) ? t(key) : code;
};
const statusLabel = status => translatedCode('STATUS', status);
const reasonLabel = reason => translatedCode('REASON', reason);

const providerLabel = device => {
  const key = `BONBOZ.PUSH_REPORT.PROVIDER_${(device.provider || 'other').toUpperCase()}`;
  return te(key) ? t(key) : device.provider;
};
const deviceTypeLabel = type => {
  if (type === 'phone') return t('BONBOZ.PUSH_REPORT.DEVICE_PHONE');
  if (type === 'desktop') return t('BONBOZ.PUSH_REPORT.DEVICE_DESKTOP');
  return '';
};

const shownCount = row => (row.shown_base ? row.shown : '—');
const shownPercent = row =>
  row.shown_base ? `${Math.round((row.shown * 100) / row.shown_base)}%` : '—';

// ---- Gỡ máy ----
const retireDialogRef = ref(null);
const deviceToRetire = ref(null);
const isRetiring = ref(false);

const retireDescription = computed(() => {
  const device = deviceToRetire.value;
  if (!device) return '';
  return t('BONBOZ.PUSH_REPORT.RETIRE_CONFIRM_DESCRIPTION', {
    user: device.user_name,
    device: [providerLabel(device), device.os_version]
      .filter(Boolean)
      .join(' · '),
  });
});

const askRetire = device => {
  deviceToRetire.value = device;
  retireDialogRef.value?.open();
};

const confirmRetire = async () => {
  const device = deviceToRetire.value;
  if (!device || isRetiring.value) return;
  isRetiring.value = true;
  try {
    await PushReportAPI.retireDevice(device.id);
    useAlert(t('BONBOZ.PUSH_REPORT.RETIRE_SUCCESS'));
    retireDialogRef.value?.close();
    emit('retired', device);
  } catch {
    useAlert(t('BONBOZ.PUSH_REPORT.RETIRE_ERROR'));
  } finally {
    isRetiring.value = false;
  }
};
</script>

<template>
  <p v-if="!devices.length" class="m-0 text-sm text-n-slate-11">
    {{ $t('BONBOZ.PUSH_REPORT.NO_DEVICES') }}
  </p>
  <div v-else class="flex flex-col gap-2">
    <p v-if="!activeDevices.length" class="m-0 text-sm text-n-slate-11">
      {{ $t('BONBOZ.PUSH_REPORT.NO_ACTIVE_DEVICES') }}
    </p>
    <div
      v-if="visibleDevices.length"
      class="overflow-x-auto"
      data-testid="push-device-table"
    >
      <table class="w-full text-sm border-collapse">
        <thead>
          <tr class="text-xs text-left text-n-slate-11 whitespace-nowrap">
            <th class="py-2 pr-3 font-medium min-w-[7rem]">
              {{ $t('BONBOZ.PUSH_REPORT.COL_USER') }}
            </th>
            <th class="py-2 pr-3 font-medium min-w-[9rem]">
              {{ $t('BONBOZ.PUSH_REPORT.COL_DEVICE') }}
            </th>
            <th class="py-2 pr-3 font-medium min-w-[5rem]">
              {{ $t('BONBOZ.PUSH_REPORT.COL_OS') }}
            </th>
            <th class="py-2 pr-3 font-medium min-w-[7rem]">
              {{ $t('BONBOZ.PUSH_REPORT.COL_STATUS') }}
            </th>
            <th class="py-2 pr-3 font-medium min-w-[6rem]">
              {{ $t('BONBOZ.PUSH_REPORT.COL_REASON') }}
            </th>
            <th class="py-2 pr-3 font-medium">
              {{ $t('BONBOZ.PUSH_REPORT.COL_STATUS_CHANGED') }}
            </th>
            <th class="py-2 pr-3 font-medium">
              {{ $t('BONBOZ.PUSH_REPORT.COL_REGISTERED') }}
            </th>
            <th class="py-2 pr-3 font-medium text-right">
              {{ $t('BONBOZ.PUSH_REPORT.SENT') }}
            </th>
            <th class="py-2 pr-3 font-medium text-right">
              {{ $t('BONBOZ.PUSH_REPORT.RECEIVED') }}
            </th>
            <th class="py-2 pr-3 font-medium text-right">
              {{ $t('BONBOZ.PUSH_REPORT.SHOWN') }}
            </th>
            <th class="py-2 pr-3 font-medium text-right">
              {{ $t('BONBOZ.PUSH_REPORT.SHOW_FAILED') }}
            </th>
            <th class="py-2 pr-3 font-medium">
              {{ $t('BONBOZ.PUSH_REPORT.COL_LAST_RECEIVED') }}
            </th>
            <th class="py-2 pr-3 font-medium">
              {{ $t('BONBOZ.PUSH_REPORT.COL_LAST_ACK') }}
            </th>
            <th class="py-2 pr-3 font-medium">
              {{ $t('BONBOZ.PUSH_REPORT.COL_LAST_SEEN') }}
            </th>
            <th class="py-2 pr-3 font-medium min-w-[8rem]">
              {{ $t('BONBOZ.PUSH_REPORT.COL_SUBSCRIBE_ERROR') }}
            </th>
            <th class="py-2 font-medium" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="device in visibleDevices"
            :key="device.id"
            class="border-t border-n-weak text-n-slate-12 whitespace-nowrap"
            :class="isInactive(device) && 'opacity-50'"
            :data-inactive="isInactive(device) || undefined"
            data-testid="push-device-row"
          >
            <td class="py-2 pr-3">{{ device.user_name }}</td>
            <td class="py-2 pr-3">
              {{ providerLabel(device) }}
              <span
                v-if="deviceTypeLabel(device.device_type)"
                class="text-xs text-n-slate-11"
              >
                {{ deviceTypeLabel(device.device_type) }}
              </span>
              <span
                v-if="device.suspect_dead && !isInactive(device)"
                class="ml-1 px-1.5 py-0.5 text-xs rounded bg-n-ruby-3 text-n-ruby-11"
              >
                {{ $t('BONBOZ.PUSH_REPORT.SUSPECT_DEAD') }}
              </span>
            </td>
            <td class="py-2 pr-3">
              <span
                class="block max-w-[8rem] truncate"
                :title="device.os_version"
                data-testid="push-device-os"
              >
                {{ device.os_version || '—' }}
              </span>
            </td>
            <td class="py-2 pr-3">
              <span
                class="px-1.5 py-0.5 text-xs rounded"
                :class="statusChipClass(device.status)"
                data-testid="push-device-status"
              >
                {{ statusLabel(device.status) }}
              </span>
            </td>
            <td class="py-2 pr-3 text-xs text-n-slate-11">
              <span
                class="block max-w-[10rem] truncate"
                :title="
                  device.status_reason && reasonLabel(device.status_reason)
                "
                data-testid="push-device-reason"
              >
                {{ reasonLabel(device.status_reason) }}
              </span>
            </td>
            <td class="py-2 pr-3 tabular-nums">
              {{ displayDate(device.status_changed_at) }}
            </td>
            <td class="py-2 pr-3 tabular-nums">
              {{ displayDate(device.registered_at) }}
            </td>
            <td class="py-2 pr-3 text-right tabular-nums">
              {{ device.sent ?? '—' }}
            </td>
            <td class="py-2 pr-3 text-right tabular-nums">
              {{ device.received ?? '—' }}
            </td>
            <td class="py-2 pr-3 text-right tabular-nums">
              {{ shownCount(device) }}
              <span v-if="device.shown_base" class="text-xs text-n-slate-11">
                {{ shownPercent(device) }}
              </span>
            </td>
            <td
              class="py-2 pr-3 text-right tabular-nums"
              :class="device.show_failed && 'text-n-ruby-11'"
            >
              {{ device.show_failed ?? '—' }}
            </td>
            <td class="py-2 pr-3 tabular-nums">
              {{ displayDate(device.last_received_at) }}
            </td>
            <td class="py-2 pr-3 tabular-nums">
              {{ displayDate(device.last_ack_at) }}
            </td>
            <td class="py-2 pr-3 tabular-nums">
              {{ displayDate(device.last_seen_at) }}
            </td>
            <td
              class="py-2 pr-3 text-xs"
              :class="
                device.last_subscribe_error
                  ? 'text-n-ruby-11'
                  : 'text-n-slate-11'
              "
            >
              <span
                class="block max-w-[14rem] truncate"
                :title="device.last_subscribe_error"
                data-testid="push-device-subscribe-error"
              >
                {{ device.last_subscribe_error || '—' }}
              </span>
            </td>
            <td class="py-2 text-right">
              <button
                v-if="!isInactive(device)"
                class="px-2 py-1 text-xs rounded-lg text-n-ruby-11 hover:bg-n-ruby-3"
                data-testid="push-device-retire"
                @click="askRetire(device)"
              >
                {{ $t('BONBOZ.PUSH_REPORT.RETIRE') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <button
      v-if="inactiveDevices.length"
      type="button"
      class="self-start px-2 py-1 text-xs rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
      :aria-expanded="showInactive"
      data-testid="push-device-toggle-inactive"
      @click="showInactive = !showInactive"
    >
      {{
        showInactive
          ? $t('BONBOZ.PUSH_REPORT.HIDE_INACTIVE_DEVICES')
          : $t('BONBOZ.PUSH_REPORT.SHOW_INACTIVE_DEVICES', {
              count: inactiveDevices.length,
            })
      }}
    </button>
  </div>

  <Dialog
    ref="retireDialogRef"
    type="alert"
    :title="$t('BONBOZ.PUSH_REPORT.RETIRE_CONFIRM_TITLE')"
    :description="retireDescription"
    :confirm-button-label="$t('BONBOZ.PUSH_REPORT.RETIRE')"
    :is-loading="isRetiring"
    @confirm="confirmRetire"
  />
</template>
